import cx from 'classnames'
import type React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { isFieldHighlighted } from '@helpers'

import { InputLabel } from '../InputElements'
import { FormControl } from './FormControl'
import { FormHelperText } from './FormHelperText'
import type { AsyncSelectInputProps } from './SelectInputs'
import { AsyncSelectInput } from './SelectInputs'

export interface ReactHookFormSearchSelectFieldProps<Option extends Record<string, unknown>>
  extends Omit<AsyncSelectInputProps<Option>, 'value' | 'onChange' | 'onBlur'> {
  disabled?: boolean
  highlighted?: boolean
  isLabelHighlighted?: boolean
  label?: React.ReactNode
  required?: boolean
  SelectComponent?: React.ComponentType<AsyncSelectInputProps<Option>>
}

export function ReactHookFormSearchSelectField<Option extends Record<string, unknown>>({
  className,
  disabled = false,
  highlighted = false,
  isLabelHighlighted = false,
  label,
  name,
  required,
  SelectComponent = AsyncSelectInput,
  ...rest
}: ReactHookFormSearchSelectFieldProps<Option>) {
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext()
  const isFieldDisabled = disabled || isSubmitting

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => {
        const hasError = !!error
        return (
          <FormControl className={className} error={hasError}>
            {label && (
              <InputLabel highlighted={isLabelHighlighted} htmlFor={name} error={hasError} required={required}>
                {label}
              </InputLabel>
            )}
            <SelectComponent
              className={cx({
                error: hasError,
                highlighted: isFieldHighlighted(highlighted, value),
              })}
              isDisabled={isFieldDisabled}
              name={name}
              // input controls
              onBlur={onBlur}
              onChange={onChange}
              value={value}
              {...rest}
            />
            {error?.message && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>
        )
      }}
    />
  )
}
